import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  Box,
  Button,
  IconButton,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material';

import { CatalogConfirmDialog } from './CatalogConfirmDialog';
import { CatalogFilterBar } from './CatalogFilterBar';
import { CatalogFooter } from './CatalogFooter';
import {
  actionVerbs,
  applyCatalogFilters,
  collectOwnerOptions,
  describeVisibleCount,
  pruneSelection,
  selectionKind,
  sortCatalogRows,
  splitSelection,
  summarizeSelection,
} from './catalogLogic';
import { CatalogTable } from './CatalogTable';
import type {
  CatalogInteractionHandler,
  CatalogNotifier,
} from './ResourceCatalog.interactions';
import type {
  CatalogFilterState,
  CatalogRow,
  CatalogSortState,
  ResourceCatalogProps,
} from './ResourceCatalog.types';

type CatalogAction = 'archive' | 'restore';

/**
 * Component props: the pinned adapter contract plus the optional interaction
 * seam (additive — adapters compiled against ResourceCatalogProps are
 * unaffected). See ResourceCatalog.interactions.ts.
 */
export interface ResourceCatalogComponentProps extends ResourceCatalogProps {
  /**
   * Surfaces catalog-internal interactions (filter/sort/expansion/undo) for
   * host-side analytics. Verb outcomes are already observable via onAction.
   */
  onInteraction?: CatalogInteractionHandler;
  /**
   * Delegates verb-outcome toasts to the host (e.g. notistack) so they
   * match the rest of the app. Omit to use the built-in Snackbar
   * (Storybook and tests need no provider).
   */
  notifier?: CatalogNotifier;
}

interface ConfirmState {
  action: CatalogAction;
  ids: string[];
}

interface SnackbarState {
  severity: 'success' | 'error';
  message: string;
  /** The inverse verb + ids; null hides the Undo affordance. */
  undo: { action: CatalogAction; ids: string[] } | null;
}

const DEFAULT_FILTERS: CatalogFilterState = {
  scope: 'all',
  owners: [],
  search: '',
  showArchived: false,
};

const DEFAULT_SORT: CatalogSortState = { key: 'usage', direction: 'desc' };

const DEFAULT_HIGH_USAGE_THRESHOLD = 100;

const INVERSE_ACTION: Record<CatalogAction, CatalogAction> = {
  archive: 'restore',
  restore: 'archive',
};

/**
 * Generic resource catalog (GDN-2335): one table for shared + personal rows,
 * faceted filtering, bulk archive/restore with confirm + undo. Resource
 * vocabulary arrives exclusively through the labels prop; mutations through
 * the verbs prop. Self-contained MUI Snackbar — no notistack provider needed.
 */
export const ResourceCatalog: React.FC<ResourceCatalogComponentProps> = ({
  rows,
  verbs,
  labels,
  renderDetail,
  loading = false,
  highUsageThreshold = DEFAULT_HIGH_USAGE_THRESHOLD,
  onAction,
  onInteraction,
  toolbarAction,
  notifier,
}) => {
  const [filters, setFilters] = useState<CatalogFilterState>(DEFAULT_FILTERS);
  const [sort, setSort] = useState<CatalogSortState>(DEFAULT_SORT);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [pending, setPending] = useState(false);
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);
  const [snackbar, setSnackbar] = useState<SnackbarState | null>(null);

  const visibleRows = useMemo(
    () => sortCatalogRows(applyCatalogFilters(rows, filters), sort),
    [rows, filters, sort]
  );
  const ownerOptions = useMemo(() => collectOwnerOptions(rows), [rows]);

  // Selection may never reference hidden rows: derive the pruned view for
  // every read, and sync state whenever visibility changes (filters or rows).
  const effectiveSelection = useMemo(
    () => pruneSelection(visibleRows, selectedIds),
    [visibleRows, selectedIds]
  );

  useEffect(() => {
    setSelectedIds((prev) => {
      const pruned = pruneSelection(visibleRows, prev);
      // pruneSelection only removes — equal length means identical content.
      return pruned.length === prev.length ? prev : pruned;
    });
  }, [visibleRows]);

  const selectionSplit = useMemo(
    () => splitSelection(rows, effectiveSelection),
    [rows, effectiveSelection]
  );

  const handleFiltersChange = useCallback(
    (next: CatalogFilterState): void => {
      setFilters(next);
      onInteraction?.({ type: 'filter_changed', filters: next });
    },
    [onInteraction]
  );

  const handleSortChange = useCallback(
    (next: CatalogSortState): void => {
      setSort(next);
      onInteraction?.({ type: 'sort_changed', sort: next });
    },
    [onInteraction]
  );

  const handleExpansionChange = useCallback(
    (id: string, expanded: boolean): void => {
      onInteraction?.({
        type: expanded ? 'row_expanded' : 'row_collapsed',
        id,
      });
    },
    [onInteraction]
  );

  const countedNoun = useCallback(
    (count: number): string =>
      `${count} ${count === 1 ? labels.noun : labels.nounPlural}`,
    [labels.noun, labels.nounPlural]
  );

  // The undo callback handed to an external notifier must survive stale
  // closures (the toast can outlive several renders) — call through a ref.
  const runVerbRef = useRef<
    (
      action: CatalogAction,
      ids: string[],
      options: { undoable: boolean }
    ) => Promise<void>
  >(() => Promise.resolve());

  /** Shared undo executor: tracks the interaction, runs the inverse verb. */
  const runUndo = useCallback(
    (action: CatalogAction, ids: string[]): void => {
      onInteraction?.({ type: 'undo_clicked', action, ids });
      void runVerbRef.current(action, ids, { undoable: false });
    },
    [onInteraction]
  );

  /** Delegate to the host's notifier when present; built-in otherwise. */
  const notify = useCallback(
    (
      severity: 'success' | 'error',
      message: string,
      undo: { action: CatalogAction; ids: string[] } | null
    ): void => {
      if (notifier) {
        notifier({
          severity,
          message,
          onUndo: undo
            ? (): void => runUndo(undo.action, undo.ids)
            : undefined,
        });
        return;
      }
      setSnackbar({ severity, message, undo });
    },
    [notifier, runUndo]
  );

  const runVerb = useCallback(
    async (
      action: CatalogAction,
      ids: string[],
      options: { undoable: boolean }
    ): Promise<void> => {
      setPending(true);
      const kind = selectionKind(rows, ids);
      try {
        await verbs[action](ids);
        onAction?.(action, ids);
        setSelectedIds((prev) => prev.filter((id) => !ids.includes(id)));
        notify(
          'success',
          `${actionVerbs(action, kind).past} ${countedNoun(ids.length)}`,
          options.undoable ? { action: INVERSE_ACTION[action], ids } : null
        );
      } catch {
        // Selection is retained so the user can retry.
        notify(
          'error',
          `Couldn't ${actionVerbs(action, kind).present.toLowerCase()} ${countedNoun(ids.length)}. Try again.`,
          null
        );
      } finally {
        setPending(false);
      }
    },
    [verbs, onAction, countedNoun, notify, rows]
  );

  useEffect(() => {
    runVerbRef.current = runVerb;
  }, [runVerb]);

  const handleToggleRow = useCallback((id: string): void => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  }, []);

  const handleToggleAll = useCallback((): void => {
    const visibleIds = visibleRows.map((row) => row.id);
    setSelectedIds(
      effectiveSelection.length === visibleIds.length ? [] : visibleIds
    );
  }, [visibleRows, effectiveSelection]);

  const handleBulk = useCallback(
    (action: CatalogAction): void => {
      const ids =
        action === 'archive'
          ? selectionSplit.activeIds
          : selectionSplit.archivedIds;
      if (ids.length > 0) setConfirm({ action, ids });
    },
    [selectionSplit]
  );

  const handleRowAction = useCallback(
    (row: CatalogRow): void => {
      const action: CatalogAction = row.archived ? 'restore' : 'archive';
      const needsConfirm =
        row.scope === 'shared' || row.usage >= highUsageThreshold;
      if (needsConfirm) {
        setConfirm({ action, ids: [row.id] });
      } else {
        void runVerb(action, [row.id], { undoable: true });
      }
    },
    [highUsageThreshold, runVerb]
  );

  const handleConfirm = useCallback(async (): Promise<void> => {
    if (confirm === null) return;
    await runVerb(confirm.action, confirm.ids, { undoable: true });
    setConfirm(null);
  }, [confirm, runVerb]);

  const handleUndo = useCallback((): void => {
    if (snackbar?.undo == null) return;
    runUndo(snackbar.undo.action, snackbar.undo.ids);
  }, [snackbar, runUndo]);

  const handleSnackbarClose = useCallback(
    (_event: unknown, reason?: string): void => {
      if (reason === 'clickaway') return;
      setSnackbar(null);
    },
    []
  );

  const emptyState =
    rows.length === 0 ? (
      <Stack spacing={0.5} sx={{ alignItems: 'center', py: 6 }}>
        <Typography variant="subtitle1">No {labels.nounPlural} yet</Typography>
        <Typography variant="body2" color="text.secondary">
          New {labels.nounPlural} will show up here.
        </Typography>
      </Stack>
    ) : (
      <Stack spacing={1} sx={{ alignItems: 'center', py: 6 }}>
        <Typography variant="subtitle1">
          No {labels.nounPlural} match your filters
        </Typography>
        <Button
          size="small"
          onClick={(): void => handleFiltersChange(DEFAULT_FILTERS)}
        >
          Clear filters
        </Button>
      </Stack>
    );

  return (
    // Fills a height-constrained parent (height: 100% resolves to auto in an
    // unconstrained flow, where the table's flex-basis fallback applies).
    <Stack spacing={2} sx={{ height: '100%', minHeight: 0 }}>
      <CatalogFilterBar
        filters={filters}
        onChange={handleFiltersChange}
        ownerOptions={ownerOptions}
        labels={labels}
        action={toolbarAction}
      />

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: '1 1 auto',
          minHeight: 0,
        }}
      >
        <CatalogTable
          rows={visibleRows}
          labels={labels}
          sort={sort}
          onSortChange={handleSortChange}
          selectedIds={effectiveSelection}
          onToggleRow={handleToggleRow}
          onToggleAll={handleToggleAll}
          onRowAction={handleRowAction}
          pending={pending}
          loading={loading}
          renderDetail={renderDetail}
          emptyState={emptyState}
          onExpansionChange={handleExpansionChange}
        />

        {!loading && rows.length > 0 && (
          <CatalogFooter
            activeCount={selectionSplit.activeIds.length}
            archivedCount={selectionSplit.archivedIds.length}
            pending={pending}
            countText={describeVisibleCount(
              rows,
              visibleRows,
              filters,
              labels.nounPlural
            )}
            onArchive={(): void => handleBulk('archive')}
            onRestore={(): void => handleBulk('restore')}
            onClearSelection={(): void => setSelectedIds([])}
          />
        )}
      </Box>

      {confirm !== null && (
        <CatalogConfirmDialog
          open
          action={confirm.action}
          summary={summarizeSelection(rows, confirm.ids, highUsageThreshold)}
          labels={labels}
          highUsageThreshold={highUsageThreshold}
          busy={pending}
          onConfirm={(): void => void handleConfirm()}
          onCancel={(): void => setConfirm(null)}
        />
      )}

      {snackbar !== null && (
        <Snackbar
          open
          autoHideDuration={pending ? null : 6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            severity={snackbar.severity}
            variant="filled"
            action={
              <>
                {snackbar.undo !== null && (
                  <Button
                    color="inherit"
                    size="small"
                    disabled={pending}
                    onClick={handleUndo}
                  >
                    Undo
                  </Button>
                )}
                <IconButton
                  size="small"
                  color="inherit"
                  aria-label="Dismiss notification"
                  onClick={(): void => setSnackbar(null)}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </>
            }
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      )}
    </Stack>
  );
};

ResourceCatalog.displayName = 'ResourceCatalog';
